import { useContext, useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { BillStatus, BillType, IBillServiceFee, IdBill } from 'realhaus-sdk';
import PaymentIcon from '@material-ui/icons/Payment';
import { RootStoreContext } from '../../../global/storeContext';
import { useStyles } from './billStyles';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Grid,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    List,
    ListItem,
    CircularProgress,
} from '@material-ui/core';
import { format } from 'date-fns';
import { IsValidNumber } from '../../../global/util';
import { Stack } from '@mui/material';
import { billTypeLabel, isBillPaymentInProgress } from '../../../utils/bill';
import { formatCurrency, serviceFeeCalculationText } from '../../../utils/transaction';

const resolveBillStatus = (bill: IdBill, isPaymentInProgress: boolean) => {
    switch (bill.status) {
        case BillStatus.CANCELED:
            return 'Canceled';
        case BillStatus.PENDING:
            if (isPaymentInProgress) return 'In Progress';
            return bill.dueDate < Date.now() ? 'Overdue' : 'Pending';
        case BillStatus.PAID:
            return 'Paid';
        default:
            return 'Pending';
    }
};

export const LeaseBillSnapshot: React.FC<{ initBill: IdBill; onPayBillClicked: () => void }> =
    observer(({ initBill, onPayBillClicked }) => {
        const classes = useStyles();
        const { uiStore, leaseStore, walletStore } = useContext(RootStoreContext);

        const [bill, setBill] = useState<IdBill>(initBill);

        const [payDisabled, setPayDisabled] = useState(true);
        // Paid by the tenant but not yet settled by the payment vendor.
        const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
        const isBillCanceled = () => {
            return bill.status === BillStatus.CANCELED;
        };

        const billStatus = resolveBillStatus(bill, isPaymentInProgress);

        useEffect(() => {
            leaseStore.subscribeToBill(initBill.leaseId, initBill.id, (b) => {
                if (!b) return;
                setBill({ id: initBill.id, ...b });
            });
        }, []);

        useEffect(() => {
            if (!bill) return;

            const isSettled = bill.status === BillStatus.PAID || bill.status === BillStatus.CANCELED;

            if (bill.status === BillStatus.PENDING && !!bill.trxLogRef) {
                walletStore
                    .getTransactionLog(bill.trxLogRef)
                    .then((trx) => {
                        const inProgress = isBillPaymentInProgress(bill, trx);
                        setIsPaymentInProgress(inProgress);
                        // A missing transaction log keeps the bill locked, as before.
                        setPayDisabled(inProgress || !trx);
                    })
                    .catch(() => {
                        setIsPaymentInProgress(false);
                        setPayDisabled(true);
                    });
            } else {
                setIsPaymentInProgress(false);
                setPayDisabled(isSettled || !!bill.trxLogRef);
            }
        }, [bill]);
        return (
            <>
                <Box
                    className={classes.cardParent}
                    style={{
                        backgroundColor: 'transparent',
                    }}
                >
                    <Grid container direction='row' spacing={2}>
                        <Grid item xs={12} md={3} className={classes.cardContent}>
                            <Typography
                                variant='body1'
                                className={
                                    isBillCanceled() ? classes.disabledColor : classes.successColor
                                }
                            >
                                <b>${bill.amount}</b>
                            </Typography>
                            <Typography variant='subtitle1' color='textSecondary'>
                                {bill.type.replace('_', ' ')}
                            </Typography>
                        </Grid>

                        <Grid item xs={4} md={2} className={classes.cardContent}>
                            <Typography
                                variant='body1'
                                style={{ textTransform: 'uppercase' }}
                                className={isBillCanceled() ? classes.disabledColor : ''}
                            >
                                <b>{billStatus.replace('_', ' ')}</b>
                            </Typography>
                            <Typography variant='subtitle1' color='textSecondary'>
                                STATUS
                            </Typography>
                        </Grid>

                        <Grid item xs={8} md={4}>
                            <Grid container direction='row' justifyContent='center'>
                                <Grid item xs={6} className={classes.cardContent}>
                                    <Typography
                                        variant='body1'
                                        style={{
                                            color:
                                                billStatus === 'Overdue'
                                                    ? 'red'
                                                    : isBillCanceled()
                                                    ? 'rgba(0, 0, 0, 0.26)'
                                                    : '',
                                        }}
                                    >
                                        <b>{format(bill.dueDate, 'PP')}</b>
                                    </Typography>
                                    <Typography variant='subtitle1' color='textSecondary'>
                                        DUE DATE
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Grid container justifyContent='center'>
                                <BillActionButtonForTenant
                                    disabled={payDisabled || uiStore.loading}
                                    onClicked={onPayBillClicked}
                                />
                                <BillActionButtonForLandlord
                                    bill={bill}
                                    disabled={payDisabled || uiStore.loading}
                                />
                            </Grid>
                        </Grid>

                        {bill.type === BillType.OTHER_FEE && (
                            <>
                                <Grid item xs={12} md={6} className={classes.cardContent}>
                                    <Typography
                                        variant='body1'
                                        className={isBillCanceled() ? classes.disabledColor : ''}
                                    >
                                        <b>Note</b>
                                    </Typography>
                                    <Typography variant='subtitle1' color='textSecondary'>
                                        {bill.note ?? ''}
                                    </Typography>
                                </Grid>
                                {bill.attachments && bill.attachments.length > 0 && (
                                    <>
                                        <Grid item xs={12} md={3} className={classes.cardContent}>
                                            <Typography
                                                variant='body1'
                                                className={
                                                    isBillCanceled() ? classes.disabledColor : ''
                                                }
                                            >
                                                <b>Attachment(s)</b>
                                            </Typography>

                                            {bill.attachments.map((attach, index) => (
                                                <List>
                                                    <ListItem key={index}>
                                                        <Link href={attach.url} target='_blank'>
                                                            Attachment {` - ${index + 1}`}
                                                        </Link>
                                                    </ListItem>
                                                </List>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </Grid>
                </Box>
            </>
        );
    });

const BillActionButtonForTenant: React.FC<{ disabled: boolean; onClicked: () => void }> = observer(
    ({ disabled, onClicked }) => {
        const { uiStore } = useContext(RootStoreContext);
        const classes = useStyles();

        return uiStore.isTenantMode() ? (
            <Button
                variant='outlined'
                color='primary'
                fullWidth
                className={classes.successColor}
                onClick={onClicked}
                disabled={disabled}
            >
                <PaymentIcon />
                Pay
            </Button>
        ) : (
            <></>
        );
    },
);

// A name like "Rent" or "Move-in fee" in lower case for mid-sentence use, leaving "NSF fee" as is.
const lowerFirst = (text: string) =>
    /^[A-Z][a-z]/.test(text) ? text[0].toLowerCase() + text.slice(1) : text;

/**
 * What marking a bill paid costs the landlord, stated before they confirm. The server works it out
 * with the same rules as the charge; if it can't be reached the dialog keeps the general notice.
 */
const ServiceFeeNotice: React.FC<{ fee: IBillServiceFee | 'loading' | 'unavailable' }> = ({
    fee,
}) => {
    if (fee === 'loading') {
        return (
            <Box display='flex' alignItems='center' mb={2}>
                <CircularProgress size={16} />
                <Box ml={1}>Checking the service fee…</Box>
            </Box>
        );
    }
    if (fee === 'unavailable') {
        return (
            <>
                Marking this bill as paid may incur a service charge in accordance with our
                payment, terms and services. <br />
                <br />
            </>
        );
    }
    if (!fee.applies) {
        return (
            <>
                No service fee applies to this bill. <br />
                <br />
            </>
        );
    }
    const calculation = serviceFeeCalculationText(fee.calculation);
    return (
        <>
            A {formatCurrency(fee.amount)} service fee applies to this rent
            {calculation ? ` (${calculation})` : ''}. Realhaus deducts it from your next payout.{' '}
            <br />
            <br />
        </>
    );
};

const BillActionButtonForLandlord: React.FC<{ bill: IdBill; disabled: boolean }> = observer(
    ({ bill, disabled }) => {
        const { uiStore, walletStore } = useContext(RootStoreContext);
        const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
        const [statusToUpdate, setStatusToUpdate] = useState<BillStatus | undefined>();
        const [serviceFee, setServiceFee] = useState<IBillServiceFee | 'loading' | 'unavailable'>(
            'loading',
        );
        const classes = useStyles();

        const confirmMarkPaid = () => {
            setStatusToUpdate(BillStatus.PAID);
            setOpenConfirmDialog(true);

            // Only rent carries a service fee.
            if (bill.type !== BillType.RENT) {
                setServiceFee({ applies: false, amount: 0 });
                return;
            }
            setServiceFee('loading');
            walletStore
                .getBillServiceFee(bill.leaseId, bill.id)
                .then((fee) => setServiceFee(fee ?? 'unavailable'))
                .catch(() => setServiceFee('unavailable'));
        };

        const billName =
            bill.type === BillType.OTHER_FEE
                ? 'bill'
                : `${lowerFirst(billTypeLabel(bill.type))} bill`;
        const statusName = statusToUpdate === BillStatus.CANCELED ? 'cancelled' : 'paid';

        const handleIsPaidBillPayment = () => {
            setOpenConfirmDialog(true);
        };

        const closeBillPaymentDialog = () => {
            setOpenConfirmDialog(false);
        };

        const numberToCurrency = (price: number) => {
            if (!IsValidNumber(price)) price = 0;
            return (
                <>
                    <NumericFormat
                        value={price}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix='$'
                        decimalSeparator='.'
                        fixedDecimalScale
                    />
                </>
            );
        };

        const handleAction = () => {
            closeBillPaymentDialog();

            if (!statusToUpdate) return;

            switch (statusToUpdate) {
                case BillStatus.PAID: {
                    handleMarkBillAsPaid();
                    break;
                }
                case BillStatus.CANCELED: {
                    handleCancelBill();
                    break;
                }
            }
        };
        const handleMarkBillAsPaid = () => {
            uiStore.showLoading();
            walletStore
                .updateBillStatusAsPaid(bill.leaseId, bill.id)
                .then(() => {
                    bill.status = BillStatus.PAID;
                    uiStore.success('Bill has been marked paid');
                })
                .catch((err) => {
                    console.error(`Could not mark bill of amount ${bill.amount} as paid`);
                    console.error(err);
                    uiStore.error(err instanceof Error ? err.message : 'Could not mark the bill paid');
                })
                .finally(() => {
                    uiStore.hideLoading();
                });
        };

        const handleCancelBill = () => {
            uiStore.showLoading();
            walletStore
                .updateBillStatusAsCancelled(bill.leaseId, bill.id)
                .then(() => {
                    bill.status = BillStatus.CANCELED;
                    uiStore.success('Bill has been cancelled');
                })
                .catch((err) => {
                    console.error(`Could not cancel bill`);
                    console.error(err);
                    uiStore.error(err instanceof Error ? err.message : 'Could not cancel the bill');
                })
                .finally(() => {
                    uiStore.hideLoading();
                });
        };

        return (
            <>
                {uiStore.isLandlordMode() && (
                    <Stack direction='row' spacing={1} width={'100%'}>
                        <Button
                            variant='outlined'
                            color='primary'
                            fullWidth
                            className={classes.successColor}
                            onClick={confirmMarkPaid}
                            disabled={disabled}
                        >
                            <PaymentIcon />
                            &nbsp; Paid
                        </Button>

                        <Button
                            variant='outlined'
                            color='secondary'
                            fullWidth
                            onClick={() => {
                                setStatusToUpdate(BillStatus.CANCELED);
                                setOpenConfirmDialog(true);
                            }}
                            disabled={disabled}
                        >
                            Cancel
                        </Button>
                    </Stack>
                )}

                <Dialog open={openConfirmDialog} onClose={closeBillPaymentDialog} fullWidth={true}>
                    {/* // Landlord Marks bill as paid confirmation dialog */}
                    <>
                        <DialogTitle id='alert-dialog-title'>
                            <Typography align='center' variant='h6'>
                                Mark {billName} of {numberToCurrency(bill.amount)} as{' '}
                                {statusName}?
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography component='div'>
                                {statusToUpdate === BillStatus.PAID && (
                                    <ServiceFeeNotice fee={serviceFee} />
                                )}
                                {statusToUpdate === BillStatus.CANCELED && (
                                    <>
                                        You will not be able to make changes or receive payment to
                                        this bill once it is Cancelled. <br />
                                    </>
                                )}
                                Would you like to proceed with marking the {billName} of{' '}
                                {numberToCurrency(bill.amount)} as {statusName}?
                            </Typography>
                        </DialogContent>
                        <DialogActions style={{ padding: '16px 24px' }}>
                            <Button onClick={closeBillPaymentDialog} color='primary'>
                                Cancel
                            </Button>
                            <Button onClick={handleAction} color='primary' variant='contained'>
                                Confirm
                            </Button>
                        </DialogActions>
                    </>
                </Dialog>
            </>
        );
    },
);
